import { useState, useEffect, useCallback } from 'react'
import { rewriteApi, RewriteGuideline } from '@/services/api'

export function useGuidelineManager(selectedChannels: string[]) {
  const [channelGuidelines, setChannelGuidelines] = useState<Record<string, RewriteGuideline[]>>({})
  const [selectedGuidelineIds, setSelectedGuidelineIds] = useState<Record<string, Set<string>>>({})
  const [guidelineEdits, setGuidelineEdits] = useState<Record<string, string>>({})
  const [editingGuidelineId, setEditingGuidelineId] = useState<string | null>(null)
  const [customGuidelines, setCustomGuidelines] = useState<Record<string, string[]>>({})
  const [customGuidelineInput, setCustomGuidelineInput] = useState<Record<string, string>>({})
  const [guidelinesExpanded, setGuidelinesExpanded] = useState<Record<string, boolean>>({})
  const [loadingGuidelines, setLoadingGuidelines] = useState<Record<string, boolean>>({})

  const loadChannelGuidelines = useCallback(async (channelId: string) => {
    if (channelGuidelines[channelId]) return
    setLoadingGuidelines(prev => ({ ...prev, [channelId]: true }))
    const res = await rewriteApi.channels.get(channelId)
    if (res.success && res.data?.guidelines) {
      const guidelines = res.data.guidelines
      setChannelGuidelines(prev => ({ ...prev, [channelId]: guidelines }))
      setSelectedGuidelineIds(prev => ({
        ...prev,
        [channelId]: new Set(guidelines.filter(g => g.is_active).map(g => g.id))
      }))
    }
    setLoadingGuidelines(prev => ({ ...prev, [channelId]: false }))
  }, [channelGuidelines])

  useEffect(() => {
    selectedChannels.forEach(chId => {
      if (!channelGuidelines[chId]) loadChannelGuidelines(chId)
    })
  }, [selectedChannels])

  const selectAll = useCallback((channelId: string) => {
    const gls = channelGuidelines[channelId] || []
    setSelectedGuidelineIds(prev => ({ ...prev, [channelId]: new Set(gls.map(g => g.id)) }))
  }, [channelGuidelines])

  const deselectAll = useCallback((channelId: string) => {
    setSelectedGuidelineIds(prev => ({ ...prev, [channelId]: new Set() }))
  }, [])

  const toggleGuideline = useCallback((channelId: string, guidelineId: string) => {
    setSelectedGuidelineIds(prev => {
      const next = new Set(prev[channelId] || [])
      if (next.has(guidelineId)) next.delete(guidelineId); else next.add(guidelineId)
      return { ...prev, [channelId]: next }
    })
  }, [])

  const toggleExpanded = useCallback((channelId: string) => {
    setGuidelinesExpanded(prev => ({ ...prev, [channelId]: !(prev[channelId] ?? true) }))
  }, [])

  const startEditGuideline = useCallback((guidelineId: string, content: string) => {
    setEditingGuidelineId(guidelineId)
    setGuidelineEdits(prev => prev[guidelineId] ? prev : { ...prev, [guidelineId]: content })
  }, [])

  const stopEditGuideline = useCallback(() => {
    setEditingGuidelineId(null)
  }, [])

  const updateGuidelineEdit = useCallback((guidelineId: string, content: string) => {
    setGuidelineEdits(prev => ({ ...prev, [guidelineId]: content }))
  }, [])

  const resetGuidelineEdit = useCallback((guidelineId: string, originalContent: string) => {
    setGuidelineEdits(prev => ({ ...prev, [guidelineId]: originalContent }))
  }, [])

  const addCustomGuideline = useCallback((channelId: string) => {
    const val = (customGuidelineInput[channelId] || '').trim()
    if (!val) return
    setCustomGuidelines(prev => ({ ...prev, [channelId]: [...(prev[channelId] || []), val] }))
    setCustomGuidelineInput(prev => ({ ...prev, [channelId]: '' }))
  }, [customGuidelineInput])

  const removeCustomGuideline = useCallback((channelId: string, index: number) => {
    setCustomGuidelines(prev => ({ ...prev, [channelId]: (prev[channelId] || []).filter((_, i) => i !== index) }))
  }, [])

  const setCustomInput = useCallback((channelId: string, value: string) => {
    setCustomGuidelineInput(prev => ({ ...prev, [channelId]: value }))
  }, [])

  const getGuidelineParams = useCallback(() => {
    const guidelineIdsParam: Record<string, string[]> = {}
    const customGuidelinesParam: Record<string, string[]> = {}
    for (const chId of selectedChannels) {
      const selected = selectedGuidelineIds[chId]
      if (selected) guidelineIdsParam[chId] = Array.from(selected)
      const customs = customGuidelines[chId]
      if (customs && customs.length > 0) customGuidelinesParam[chId] = customs
    }
    return {
      guidelineIds: Object.keys(guidelineIdsParam).length > 0 ? guidelineIdsParam : undefined,
      customGuidelines: Object.keys(customGuidelinesParam).length > 0 ? customGuidelinesParam : undefined,
    }
  }, [selectedChannels, selectedGuidelineIds, customGuidelines])

  const getGuidelinesForSection = useCallback((channelId: string, sectionType?: string) => {
    const gls = channelGuidelines[channelId] || []
    const selected = selectedGuidelineIds[channelId] || new Set<string>()
    const common = gls.filter(g => !g.section_type && selected.has(g.id))
    const sectionSpecific = sectionType ? gls.filter(g => g.section_type === sectionType && selected.has(g.id)) : []
    const customs = customGuidelines[channelId] || []
    return { common, sectionSpecific, customs }
  }, [channelGuidelines, selectedGuidelineIds, customGuidelines])

  return {
    channelGuidelines,
    selectedGuidelineIds,
    guidelineEdits,
    editingGuidelineId,
    customGuidelines,
    customGuidelineInput,
    guidelinesExpanded,
    loadingGuidelines,
    selectAll,
    deselectAll,
    toggleGuideline,
    toggleExpanded,
    startEditGuideline,
    stopEditGuideline,
    updateGuidelineEdit,
    resetGuidelineEdit,
    addCustomGuideline,
    removeCustomGuideline,
    setCustomInput,
    getGuidelineParams,
    getGuidelinesForSection,
  }
}
